/* food-safety-checker.js — vanilla progressive enhancement for the Pet Food Safety
 * Checker page (#sn-food-safety-checker [data-sn-widget="FoodChecker"]).
 *
 * The page renders a faithful static snapshot server-side (one pre-answered result);
 * this script enhances it with a live search. If the script never runs, the static
 * result + full-guide links are still visible and crawlable.
 *
 * ⚠️ Every entry in FC_DB must be checked against a veterinary source before it's
 * added — this widget must never guess a verdict for a food that isn't in the data.
 * The page is vet-reviewed and carries a reviewer byline, so adding an entry is a
 * material change: it needs Dr de Klerk's sign-off, not just a source.
 *
 * ⚠️ The Dog/Cat pills are styled with INLINE styles in the Liquid section (the page
 * ships no <style> tag, deliberately — see the super-everyday CSS-bleed incident), so
 * an [aria-pressed] CSS selector can never win. The active state has to be written
 * onto element.style here. Missing that is what made the toggle look stuck on "Dog"
 * in the 2026-08-19 vet review.
 * No dependencies. Self-contained. Safe to load with `defer`.
 */
(function () {
  'use strict';

  // <html lang> comes from request.locale.iso_code, so /de/ pages report "de".
  var SN_DE = typeof document !== 'undefined' && document.documentElement
    ? /^de/i.test(document.documentElement.getAttribute('lang') || '')
    : false;
  var T = function (en, de) { return SN_DE ? de : en; };
  // Root-relative guide links would drop a German visitor back to the English blog.
  var SN_PREFIX = SN_DE ? '/de' : '';

  // ---- Checker data — one vetted entry per food, expand as spoke guides ship ----
  var FC_DB = [
    {
      id: 'cat-food',
      aliases: ['cat food', 'catfood', 'my cats food', "my cat's food", 'kibble cat',
        'katzenfutter', 'katzennahrung', 'futter der katze', 'trockenfutter katze', 'nassfutter katze'],
      species: ['dog'],
      verdict: 'caution',
      tag: T('Caution', 'Vorsicht'),
      headline: T('Can dogs eat cat food?', 'Dürfen Hunde Katzenfutter fressen?'),
      reason: T("A few mouthfuls of cat food, once in a while, won't harm most dogs - it isn't toxic. But it's formulated for a cat's needs, not a dog's: depending on the product it can carry more calories or fat than a particular dog needs, so feeding it regularly can contribute to stomach upset and weight gain. A large amount of a high-fat cat food is the bigger worry for dogs prone to pancreatitis.",
        'Ein paar Bissen Katzenfutter ab und zu schaden den meisten Hunden nicht - giftig ist es nicht. Aber es ist auf den Bedarf einer Katze abgestimmt, nicht auf den eines Hundes: Je nach Produkt liefert es mehr Kalorien oder Fett, als ein bestimmter Hund braucht. Regelmäßig gefüttert kann das zu Magen-Darm-Problemen und Gewichtszunahme beitragen. Die größere Sorge bei Hunden mit einer Neigung zu Bauchspeicheldrüsenentzündungen ist eine große Menge fettreiches Katzenfutter.'),
      guideUrl: '/blogs/pet/can-dogs-eat-cat-food',
    },
    {
      id: 'dog-food',
      aliases: ['dog food', 'dogfood', 'my dogs food', "my dog's food", 'kibble dog',
        'hundefutter', 'hundenahrung', 'futter des hundes', 'trockenfutter hund', 'nassfutter hund'],
      species: ['cat'],
      verdict: 'caution',
      tag: T('Caution', 'Vorsicht'),
      headline: T('Can cats eat dog food?', 'Dürfen Katzen Hundefutter fressen?'),
      reason: T("A stolen bite isn't dangerous - dog food isn't toxic to cats. But it's formulated for dogs' nutritional requirements, not cats' - including taurine and several other nutrients - and a diet of dog food over months can lead to serious problems, including heart disease and progressive, eventually irreversible vision loss.",
        'Ein stibitzter Bissen ist nicht gefährlich - Hundefutter ist für Katzen nicht giftig. Aber es ist auf den Nährstoffbedarf von Hunden ausgelegt, nicht auf den von Katzen - einschließlich Taurin und mehrerer weiterer Nährstoffe - und eine über Monate auf Hundefutter aufgebaute Ernährung kann zu ernsten Problemen führen, darunter Herzerkrankungen und fortschreitender, irgendwann irreversibler Sehverlust.'),
      guideUrl: '/blogs/pet/can-cats-eat-dog-food',
    },
    {
      id: 'banana',
      aliases: ['banana', 'bananas', 'banane', 'bananen'],
      // Dogs only, deliberately. The headline, the caveats and the linked guide are all
      // dog-specific, so serving this as a *cat* answer was showing dog advice under a lit
      // Cat pill. A cat entry needs its own vet-checked copy before it goes back in.
      species: ['dog'],
      verdict: 'safe',
      tag: T('Safe', 'Unbedenklich'),
      headline: T('Can dogs eat bananas?', 'Dürfen Hunde Bananen fressen?'),
      reason: T("Yes, in moderation - banana isn't toxic and offers real nutrients like potassium and vitamin B6. Skip the peel (a blockage risk, not a poison risk), and talk to your vet about portions first if your dog is diabetic, overweight or has kidney disease.",
        'Ja, in Maßen - Bananen sind nicht giftig und liefern echte Nährstoffe wie Kalium und Vitamin B6. Die Schale gehört nicht dazu (Verstopfungsgefahr im Darm, keine Vergiftungsgefahr). Sprechen Sie vorher mit Ihrer Tierarztpraxis über die Menge, wenn Ihr Hund zuckerkrank ist, Übergewicht hat oder an einer Nierenerkrankung leidet.'),
      guideUrl: '/blogs/pet/can-dogs-eat-bananas',
    },
    {
      id: 'egg',
      aliases: ['egg', 'eggs', 'boiled egg', 'boiled eggs', 'scrambled egg', 'scrambled eggs',
        'raw egg', 'raw eggs', 'egg white', 'egg whites', 'egg yolk', 'eggshell', 'egg shell',
        'eggshells', 'omelette', 'omelet', 'fried egg', 'poached egg',
        'ei', 'eier', 'gekochtes ei', 'rohes ei', 'ruhrei', 'ruehrei', 'spiegelei',
        'eiweiss', 'eiklar', 'eigelb', 'eierschale', 'eierschalen', 'omelett'],
      species: ['dog', 'cat'],
      verdict: 'safe',
      tag: T('Safe', 'Unbedenklich'),
      headline: T('Can dogs or cats eat eggs?', 'Dürfen Hunde oder Katzen Eier fressen?'),
      reason: T("Yes - plain, cooked, and in small amounts. Egg isn't toxic and is a good source of highly digestible, complete protein, but it counts as a treat, and WSAVA's rule is that treats stay under 10% of daily calories. A medium egg is roughly 60–80 kcal depending on its size, which is most of a small dog's daily treat allowance and more than most cats'. Skip raw egg - avidin in raw egg white can reduce biotin absorption - and skip the shell.",
        'Ja - schlicht, gekocht und in kleinen Mengen. Ei ist nicht giftig und eine gute Quelle für hochverdauliches, vollwertiges Eiweiß, zählt aber als Leckerli, und die WSAVA-Regel lautet: Leckerlis bleiben unter 10 % der Tagesenergie. Ein mittelgroßes Ei hat je nach Größe rund 60 bis 80 kcal - bei einem kleinen Hund ist das fast das gesamte Tagesbudget, bei den meisten Katzen mehr als das. Rohes Ei besser weglassen (Avidin in rohem Eiklar kann die Biotinaufnahme verringern), die Schale ebenfalls.'),
      guideUrl: '/blogs/pet/can-dogs-eat-eggs',
    },
    {
      id: 'chocolate',
      aliases: ['chocolate', 'choc', 'chocolates', 'dark chocolate', 'milk chocolate',
        'white chocolate', 'baking chocolate', 'bakers chocolate', 'cooking chocolate',
        'cocoa', 'cacao', 'cocoa powder', 'chocolate bar', 'chocolate biscuit', 'brownie',
        'brownies', 'cocoa mulch', 'hot chocolate',
        'schokolade', 'schoki', 'vollmilchschokolade', 'zartbitterschokolade',
        'dunkle schokolade', 'weisse schokolade', 'bitterschokolade', 'backschokolade',
        'kakao', 'kakaopulver', 'praline', 'pralinen', 'schokoriegel'],
      species: ['dog', 'cat'],
      verdict: 'unsafe',
      tag: T('Unsafe', 'Gefährlich'),
      headline: T('Can dogs or cats eat chocolate?', 'Dürfen Hunde oder Katzen Schokolade fressen?'),
      reason: T("No. Chocolate contains theobromine and caffeine, which dogs clear far more slowly than we do. Vets work from roughly 20 mg/kg for mild signs, 40–50 mg/kg for effects on the heart and 60 mg/kg where seizures become a significant risk - so the type matters enormously, because dark and baking chocolate carry several times the theobromine of milk chocolate. Signs often don't appear for 6–12 hours, so don't wait for them. For a cat, any amount is a call to the vet rather than a sum to do at home.",
        'Nein. Schokolade enthält Theobromin und Koffein, die Hunde sehr viel langsamer abbauen als wir. In der Tiermedizin wird mit etwa 20 mg/kg für erste Anzeichen gerechnet, mit 40–50 mg/kg für Auswirkungen auf das Herz und mit 60 mg/kg, ab denen Krampfanfälle zu einem erheblichen Risiko werden - die Sorte ist deshalb entscheidend, denn Zartbitter- und Backschokolade enthalten ein Vielfaches des Theobromins von Vollmilchschokolade. Anzeichen zeigen sich oft erst nach 6 bis 12 Stunden - warten Sie also nicht darauf. Bei einer Katze ist jede Menge ein Anruf in der Tierarztpraxis, keine Rechenaufgabe für zu Hause.'),
      guideUrl: '/blogs/pet/how-much-chocolate-can-a-dog-eat',
    },
    {
      id: 'onion-garlic',
      aliases: ['onion', 'onions', 'garlic', 'onion and garlic', 'onions and garlic',
        'onion powder', 'garlic powder', 'garlic bread', 'leek', 'leeks', 'chives',
        'spring onion', 'spring onions', 'shallot', 'shallots', 'allium', 'gravy',
        'zwiebel', 'zwiebeln', 'knoblauch', 'zwiebelpulver', 'knoblauchpulver',
        'lauch', 'porree', 'schnittlauch', 'fruhlingszwiebel', 'fruehlingszwiebel',
        'schalotte', 'schalotten', 'knoblauchbrot', 'bratensosse', 'sosse'],
      species: ['dog', 'cat'],
      verdict: 'unsafe',
      tag: T('Unsafe', 'Gefährlich'),
      headline: T('Can dogs or cats eat onion or garlic?', 'Dürfen Hunde oder Katzen Zwiebeln oder Knoblauch fressen?'),
      reason: T("No. Onion, garlic, leek and chive damage red blood cells, and what makes this one dangerous is that it doesn't look dangerous at the time - the first visible signs often come several days after the meal, although the timing varies. Powder is the form behind most cases, because it's concentrated and hides in gravy, stock and leftovers. Cats are the more sensitive of the two: MSD reports poisoning after relatively small amounts, around 5 g/kg of raw onion, and in one reported case less than a teaspoon of cooked onion.",
        'Nein. Zwiebeln, Knoblauch, Lauch und Schnittlauch schädigen die roten Blutkörperchen, und das Tückische daran ist, dass es im Moment des Fressens harmlos aussieht - die ersten sichtbaren Anzeichen treten oft erst mehrere Tage später auf, wobei der Zeitpunkt variiert. Pulver steckt hinter den meisten Fällen, weil es konzentriert ist und sich in Soßen, Brühe und Resten versteckt. Katzen sind die empfindlichere der beiden Arten: MSD berichtet von Vergiftungen schon nach verhältnismäßig kleinen Mengen, etwa 5 g/kg rohe Zwiebel, und in einem berichteten Fall nach weniger als einem Teelöffel gekochter Zwiebel.'),
      guideUrl: '/blogs/pet/can-dogs-eat-onions-and-garlic',
    },
  ];

  var FC_VERDICT = {
    safe:    { bg: '#E6FFC6', fg: '#224031', label: T('Safe', 'Unbedenklich') },
    caution: { bg: '#FFF3D4', fg: '#5A4413', label: T('Caution', 'Vorsicht') },
    unsafe:  { bg: '#FBD9D0', fg: '#7A241A', label: T('Unsafe', 'Gefährlich') },
  };

  function norm(s) {
    return (s || '')
      .toLowerCase()
      .trim()
      // German input: fold umlauts and ß before the strip below removes them outright.
      // Without this, "Rührei" normalises to "rhrei" and can never match an alias.
      .replace(/ä/g, 'a').replace(/ö/g, 'o').replace(/ü/g, 'u').replace(/ß/g, 'ss')
      .replace(/[^a-z0-9 ]/g, '')
      .replace(/\s+/g, ' ');
  }

  function findEntry(query, species) {
    var q = norm(query);
    if (!q) return null;
    var candidates = FC_DB.filter(function (e) { return e.species.indexOf(species) !== -1; });
    // exact / substring alias match, longest alias first so "cat food" beats "cat"
    var best = null;
    candidates.forEach(function (e) {
      e.aliases.forEach(function (a) {
        var na = norm(a);
        if (q.indexOf(na) !== -1 || na.indexOf(q) !== -1) {
          if (!best || na.length > norm(best.matchedAlias).length) {
            best = Object.assign({}, e, { matchedAlias: a });
          }
        }
      });
    });
    return best;
  }

  var BADGE_STYLE = 'display:inline-flex;align-items:center;gap:8px;padding:7px 16px;border-radius:999px;font-family:var(--sn-display);font-weight:700;font-size:13.5px;margin-bottom:12px';
  var REASON_STYLE = 'font-size:15.5px;line-height:1.58;color:var(--sn-ink)';
  var LINK_STYLE = 'display:inline-block;margin-top:8px;font-weight:600;font-size:14.5px;color:var(--sn-forest)';

  function renderResult(container, species, query, entry) {
    if (!entry) {
      container.innerHTML =
        '<span style="' + BADGE_STYLE + ';background:#F1F0EC;color:#5A5A52">' + T('Not in our checker yet', 'Noch nicht in unserem Check') + '</span>' +
        '<p style="' + REASON_STYLE + '">' + T(
          'We haven\'t vetted "' + escapeHtml(query) + '" for ' + species + 's yet - we\'re adding foods one at a time, checked against veterinary sources. Don\'t assume it\'s safe. If your pet has already eaten it, contact your vet promptly even if they seem well, rather than waiting for symptoms - an animal poison-control service can also advise on the risk. You can check any food instantly in the Supernormal app.',
          '„' + escapeHtml(query) + '“ haben wir für ' + (species === 'cat' ? 'Katzen' : 'Hunde') + ' noch nicht geprüft - wir nehmen die Lebensmittel nacheinander auf, jedes anhand tiermedizinischer Quellen. Gehen Sie nicht davon aus, dass es unbedenklich ist. Hat Ihr Tier es bereits gefressen, wenden Sie sich zeitnah an Ihre Tierarztpraxis, auch wenn Ihr Tier gesund wirkt - warten Sie nicht auf Symptome. In der Supernormal App können Sie jedes Lebensmittel sofort prüfen.'
        ) + '</p>';
      return;
    }
    var v = FC_VERDICT[entry.verdict];
    var icon = entry.verdict === 'safe' ? '✓' : entry.verdict === 'unsafe' ? '✕' : '⚠';
    container.innerHTML =
      '<span style="' + BADGE_STYLE + ';background:' + v.bg + ';color:' + v.fg + '">' + icon + ' ' + v.label + ' - ' + escapeHtml(entry.headline) + '</span>' +
      '<p style="' + REASON_STYLE + '">' + escapeHtml(entry.reason) + '</p>' +
      (entry.guideUrl && !(SN_DE && entry.guideDeReady === false) ? '<a style="' + LINK_STYLE + '" href="' + SN_PREFIX + entry.guideUrl + '">' + T('Read the full guide →', 'Zum ausführlichen Ratgeber →') + '</a>' : '');
  }

  function escapeHtml(s) {
    var d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }

  // Mirrors the inline styles the Liquid section renders on the two pills. Written as
  // element.style, not a class, because the page deliberately ships no stylesheet.
  var PILL_ON  = { background: 'var(--sn-forest)', color: 'var(--sn-cream)' };
  var PILL_OFF = { background: 'transparent', color: 'var(--sn-ink-60)' };

  function paintPills(btns, active) {
    btns.forEach(function (b) {
      var st = b === active ? PILL_ON : PILL_OFF;
      b.style.background = st.background;
      b.style.color = st.color;
      b.setAttribute('aria-pressed', b === active ? 'true' : 'false');
    });
  }

  // With the search box empty there still has to be something on screen, and it has to
  // match the species that is actually selected — otherwise switching to Cat leaves a
  // dog answer sitting there, which is how the toggle read as broken.
  function defaultEntry(species) {
    var id = species === 'cat' ? 'dog-food' : 'cat-food';
    for (var i = 0; i < FC_DB.length; i++) {
      if (FC_DB[i].id === id && FC_DB[i].species.indexOf(species) !== -1) return FC_DB[i];
    }
    return null;
  }

  function init() {
    var widget = document.querySelector('[data-sn-widget="FoodChecker"]');
    if (!widget) return;
    var input = widget.querySelector('[data-fc-input]');
    var result = widget.querySelector('[data-fc-result]');
    var speciesBtns = Array.prototype.slice.call(widget.querySelectorAll('[data-fc-species]'));
    var species = 'dog';

    function refresh() {
      if (!result) return;
      var q = input ? input.value.trim() : '';
      if (q) renderResult(result, species, q, findEntry(q, species));
      else renderResult(result, species, '', defaultEntry(species));
    }

    speciesBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        species = btn.getAttribute('data-fc-species');
        paintPills(speciesBtns, btn);
        refresh();
      });
    });

    if (input) {
      var timer = null;
      input.addEventListener('input', function () {
        clearTimeout(timer);
        timer = setTimeout(refresh, 150);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
